import { useState, Ref, useEffect, useCallback, FormEvent, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Button, Input, DialogTrigger } from 'react-aria-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons/faAngleDown';
import { Popover } from 'components/popover/Popover';
import { NavigateRoutesEnum } from '@features/routing/routesEnum';
import usePreviousState from '@hooks/previous-state/usePreviousState';
import { setGlobalSearch } from '@store/global/slice';
import { selectGlobalSearch, selectSearchTypes } from '@store/global/selectors';
import { SearchSettings } from '@layouts/toolbar/search/search-settings/SearchSettings';



interface SearchFormProps {
	ref: Ref<HTMLFormElement | null>;
}


export const SearchForm = (props: SearchFormProps) => {
	const { ref } = props;

	const inputRef = useRef<HTMLInputElement>(null);
	const [ isTabFocused, setIsTabFocused ] = useState(false);
	const [ searchValue, setSearchValue ] = useState<string>('');
	const [ popoverWidth, setPopoverWidth ] = useState(0);

	const globalSearch = useSelector(selectGlobalSearch);
	const searchTypes = useSelector(selectSearchTypes);
	const previousGlobalSearch = usePreviousState(globalSearch);

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const updateGlobalSearch = (value: string | null) => dispatch(setGlobalSearch(value));

	useEffect(() => {
		if (!globalSearch && previousGlobalSearch) {
			setSearchValue('');
		}
	}, [ globalSearch ]);

	const handleClear = useCallback(() => {
		setSearchValue('');
		updateGlobalSearch(null);
	}, []);

	const handleSubmit = useCallback(() => {
		if (searchValue && searchValue.length > 0) {
			updateGlobalSearch(searchValue);
			navigate(NavigateRoutesEnum.search);
		}
	}, [ searchValue ]);

	const measuredRef = useCallback((elementRef: HTMLInputElement | null) => {
		if (elementRef !== null) {
			inputRef.current = elementRef;
			setPopoverWidth(elementRef.getBoundingClientRect().width);
		}
	}, []);

	return (
		<Form
			className='lfmt-search-form'
			onSubmit={(event: FormEvent<HTMLFormElement>) => {
				event.preventDefault();
				handleSubmit();
			}}
			onReset={handleClear}
			ref={ref}>

			<Input
				name='search'
				ref={measuredRef}
				type='text'
				className={`lfmt-search-input${isTabFocused ? ' tab-focused' : ''}`}
				autoCapitalize={'none'}
				autoComplete={'off'}
				autoCorrect={'off'}
				spellCheck={false}
				aria-label='Hledat'
				aria-haspopup={false}
				aria-autocomplete={'list'}
				placeholder="Vyhledávání..."
				maxLength={25}
				value={searchValue}
				onKeyDown={(event) => {
					event.key === 'Escape' && handleClear();
				}}
				
				onChange={(event) => setSearchValue(event.target.value)}
				onFocus={(event) => {
					event.relatedTarget && event.relatedTarget.id === 'basic-nav-dropdown'
					? setIsTabFocused(true)
					: setIsTabFocused(false)
				}}
				onBlur={() => setIsTabFocused(false)} />
		
			<Button className='search-input-button clear-icon-button' type='reset'>
				<FontAwesomeIcon className='search-input-icon' icon={faXmark} size='lg' />
			</Button>

			<Button className='search-input-button search-icon-button' type='submit' isDisabled={!searchTypes}>
				<FontAwesomeIcon className='search-input-icon' icon={faArrowRight} size='lg' />
			</Button>

			<DialogTrigger>
				<Button className='search-input-button settings-icon-button' type='button'>
					<FontAwesomeIcon className='search-input-icon' icon={faAngleDown} size='lg' />
				</Button>
				
				<Popover triggerRef={inputRef} showArrow={false} style={{ minWidth: popoverWidth ? `${popoverWidth}px` : 'auto' }}>
					<SearchSettings />
				</Popover>
			</DialogTrigger>
		
		</Form>
	);
}